import { useTranslation } from "react-i18next";
import { PlusCircle, Trash2 } from "lucide-react";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Input } from "@akuna/ui";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getItemTypeBadgeClass } from "@/lib/item-type-badge";
import { isRecord } from "@/components/calendar/useAppointmentDetailsController";
import type { AppointmentDetailsController } from "@/components/calendar/useAppointmentDetailsController";
import type { AppointmentItemRow } from "@/hooks/useAppointmentItems";

const formatCurrency = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function AppointmentItemsCard({ ctrl }: { ctrl: AppointmentDetailsController }) {
  const { t } = useTranslation("calendar");
  const {
    itemOptions,
    selectedItemId,
    setSelectedItemId,
    searchValue,
    setSearchValue,
    quantity,
    setQuantity,
    handleAddItem,
    displayItems,
    handleRemoveItem,
    addAppointmentItem,
    deleteAppointmentItem,
    hasActiveSale,
    appointmentId,
    availableItemsList,
    selectedItem,
  } = ctrl;

  return (
    <section className="rounded-[18px] border border-border/30 bg-card p-4 shadow-sm">
      <div className="mb-2.5 text-sm font-bold">{t("appointmentForm.productsAndServices")}</div>
      {!hasActiveSale && (
        <>
          <div className="mb-2.5 flex gap-2">
            <Autocomplete
              options={itemOptions}
              value={searchValue}
              onChange={(v) => {
                setSearchValue(v);
                if (!v.trim()) setSelectedItemId("");
              }}
              onSelect={(opt) => {
                setSelectedItemId(opt.value);
                setSearchValue(opt.label);
              }}
              selectedValue={selectedItemId}
              placeholder={t("appointmentForm.itemPlaceholder")}
              emptyMessage={t("appointmentForm.noItemResults")}
              disabled={addAppointmentItem.isPending || !appointmentId}
              className="min-w-0 flex-1"
            />
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => {
                const v = Number(e.target.value);
                setQuantity(Number.isNaN(v) ? 1 : Math.max(1, v));
              }}
              disabled={addAppointmentItem.isPending || !appointmentId}
              className="w-[54px] shrink-0 rounded-xl bg-background-app text-center"
            />
          </div>
          <Button
            onClick={handleAddItem}
            disabled={!selectedItem || !appointmentId || addAppointmentItem.isPending}
            className="h-11 w-full rounded-xl shadow-md shadow-primary/20"
          >
            <PlusCircle className="size-[17px]" /> {t("appointmentForm.add")}
          </Button>
          {selectedItem ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {t("appointmentForm.unitPrice")}: {formatCurrency(selectedItem.sale_price ?? 0)} ·{" "}
              {t("appointmentForm.estimatedTotal")}: {formatCurrency((selectedItem.sale_price ?? 0) * quantity)}
            </p>
          ) : null}
        </>
      )}

      {displayItems.length > 0 ? (
        <>
          <div className="my-3 h-px bg-border/40" />
          <ul className="flex flex-col gap-3">
            {displayItems.map((row) => {
              const kind = (() => {
                const ref = (row as AppointmentItemRow).item;
                if (isRecord(ref) && typeof ref["type"] === "string") {
                  const v = ref["type"] as string;
                  if (v === "PRODUTO") return "Produto";
                  if (v === "SERVICO") return "Serviço";
                  if (v === "KIT") return "Kit";
                  return v;
                }
                return null;
              })();
              return (
                <li key={row.id} className="flex items-start gap-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-[15px] font-bold">{row.title_snapshot}</span>
                      {kind ? (
                        <Badge variant="secondary" className={cn("h-5 shrink-0 text-xs", getItemTypeBadgeClass(kind))}>
                          {kind}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-[13px] tabular-nums text-muted-foreground">
                      {row.qty} × {formatCurrency(row._displayPrice)} ={" "}
                      <span className="font-semibold text-foreground">
                        {formatCurrency(row._displayLineTotal)}
                      </span>
                    </div>
                  </div>
                  {!hasActiveSale ? (
                    <button
                      type="button"
                      aria-label={t("appointmentDetails.deleteBelonging")}
                      onClick={() => handleRemoveItem(row as AppointmentItemRow)}
                      disabled={deleteAppointmentItem.isPending}
                      className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] border border-border/30 bg-card"
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </section>
  );
}
